import React from 'react';
import { Button } from '../../components/button';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { ScrollMarker } from '../../components/scroll-marker';
import { SearchInput } from '../../components/search-input';
import {
    applyCoordOverrides,
    diffStores,
    Difference,
    DifferenceMap,
    findSuspectCoordChanges,
    getAddedStores,
    parsedStoreToStore,
    ParsedStore,
    storeFilter,
    WEEKDAYS,
} from '../../helpers/store-helper';
import { CoordPrecisionModal } from '../../components/coord-precision-modal';
import {
    useAuthStore,
    useStoresStore,
    useApplicationSelectionStore,
} from '../../contexts/mobx';
import style from './style.module.scss';
import { observer } from 'mobx-react';
import {
    StoreBrand,
    STORE_BRAND_LABELS,
    APPLICATION_TO_BRAND,
} from '../../stores/constants';
import { useNavigation } from '../../utils/use-navigation';
import { UpsertStoresResponse } from '../../types';
import { BigModal } from '../../components/big-modal';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useSimpleMap } from '../../helpers/use-simple-map';

export const StoresDiffPage = observer(() => {
    const store = useStoresStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const brand =
        APPLICATION_TO_BRAND[applicationSelectionStore.selectedApplication];

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }
    const [filter, setFilter] = React.useState('');
    const [
        uploadResult,
        setUploadResult,
    ] = React.useState<UpsertStoresResponse | null>(null);
    const [added, setAdded] = React.useState<ParsedStore[]>([]);
    const [
        deselectionMap,
        setDeselection,
        setMultipleDeselection,
    ] = useSimpleMap<boolean>();
    const [differences, setDifferences] = React.useState<DifferenceMap>(
        new DifferenceMap()
    );
    const [showCoordModal, setShowCoordModal] = React.useState(false);

    React.useEffect(() => {
        if (store.parsedStore === null || store.stores === null) {
            navigation.replaceToStores();
            return;
        }
        const map = diffStores(store.stores, store.parsedStore);
        const added = getAddedStores(store.stores, store.parsedStore);
        setAdded(added);
        setDifferences(map);
    }, []);

    const handleCoordReviewConfirm = (keepDbNrefs: Set<string>) => {
        const original = store.originalParsedStore;
        if (!original || !store.stores) return;
        const corrected = applyCoordOverrides(
            original,
            store.stores,
            keepDbNrefs
        );
        store.setParsedStore(corrected);
        const map = diffStores(store.stores, corrected);
        const added = getAddedStores(store.stores, corrected);
        setAdded(added);
        setDifferences(map);
        store.setCoordImportState(original, findSuspectCoordChanges(map));
        setShowCoordModal(false);
    };

    const coordDifferences = React.useMemo(
        () =>
            store.originalParsedStore && store.stores
                ? diffStores(store.stores, store.originalParsedStore)
                : new DifferenceMap(),
        [store.stores, store.originalParsedStore]
    );

    const diff = Array.from(differences.values());
    const filteredDiff = diff.filter((diff) =>
        storeFilter(filter)(diff.initial)
    );

    // This is a bit of hack. I initially forgot about the added stores. However it is
    // pretty easy to reuse the Difference display to show added stores.
    const addedDiff = added
        .filter((a) => brand === undefined || a.brand === brand)
        .map((a) => new Difference(parsedStoreToStore(a)));
    const filteredAddedDiff = addedDiff.filter((diff) =>
        storeFilter(filter)(diff.initial)
    );

    const uploadChanges = async () => {
        if (!brand) return;
        const result = await store.uploadChanges({
            brand,
            additions: addedDiff
                .filter((d) => !deselectionMap[d.initial.nref])
                .map((d) => d.initial),
            modifications: diff
                .filter((d) => !deselectionMap[d.initial.nref])
                .map((d) => d.getModifiedStore()),
        });
        if (!result) return;

        const appliedCount = result.applied.added + result.applied.modified;

        if (result.skipped.length > 0) {
            if (appliedCount > 0) {
                NotificationManager.showSuccess('Stores updated!');
            }
            setUploadResult(result);
        } else {
            NotificationManager.showSuccess('Stores updated!');
            setTimeout(() => navigation.replaceToStores(), 500);
        }
    };

    const allFilteredDiff = [...filteredAddedDiff, ...filteredDiff];

    const DifferenceDisplay = React.memo(
        (props: { difference: Difference; showDifference: boolean }) => {
            const { showDifference, difference } = props;

            const initial = difference.initial;
            const hasStreetChanged = difference.street !== undefined;
            const hasTelChanged = difference.phoneNumber !== undefined;
            const hasCityChanged = difference.city !== undefined;
            const hasPostChanged = difference.post !== undefined;
            const hasStatusChanged = difference.status !== undefined;
            const hasCarWashStatusChanged =
                difference.carWashStatus !== undefined;
            const hasLngChanged = difference.lng !== undefined;
            const hasLatChanged = difference.lat !== undefined;

            const modifiedServices = initial.services
                .concat(difference.addedServices ?? [])
                .filter(
                    (service) => !difference.removedServices?.includes(service)
                );

            const displayedServices = showDifference
                ? modifiedServices
                : initial.services;

            const getServiceClass = (service: string) => {
                if (
                    showDifference &&
                    difference.addedServices?.includes(service)
                ) {
                    return style.added;
                }

                if (
                    !showDifference &&
                    difference.removedServices?.includes(service)
                ) {
                    return style.removed;
                }
            };

            return (
                <div className={style.store_box}>
                    <h3>{showDifference ? 'Modified' : 'Initial'}</h3>
                    <p>
                        <b>Title: </b>
                        {initial.addr.street}
                    </p>
                    <p>
                        <b>Tel: </b>
                        <span className={hasTelChanged ? style.changed : ''}>
                            {showDifference && hasTelChanged
                                ? difference.phoneNumber
                                : initial.tel}
                        </span>
                    </p>
                    <p>
                        <b>Street: </b>
                        <span className={hasStreetChanged ? style.changed : ''}>
                            {showDifference && hasStreetChanged
                                ? difference.street
                                : initial.addr.street}
                        </span>
                    </p>
                    <p>
                        <b>City: </b>
                        <span className={hasCityChanged ? style.changed : ''}>
                            {showDifference && hasCityChanged
                                ? difference.city
                                : initial.addr.city}
                        </span>
                    </p>
                    <p>
                        <b>Zip code: </b>
                        <span className={hasPostChanged ? style.changed : ''}>
                            {showDifference && hasPostChanged
                                ? difference.post
                                : initial.addr.post}
                        </span>
                    </p>
                    <p>
                        <b>Lat: </b>
                        <span className={hasLatChanged ? style.changed : ''}>
                            {showDifference && hasLatChanged
                                ? difference.lat
                                : initial.lat}
                        </span>
                    </p>
                    <p>
                        <b>Lng: </b>
                        <span className={hasLngChanged ? style.changed : ''}>
                            {showDifference && hasLngChanged
                                ? difference.lng
                                : initial.lng}
                        </span>
                    </p>
                    <p>
                        <b>Car Wash Status: </b>
                        <span
                            className={
                                hasCarWashStatusChanged ? style.changed : ''
                            }
                        >
                            {showDifference && hasCarWashStatusChanged
                                ? difference.carWashStatus
                                : initial.carWashStatus}
                        </span>
                    </p>
                    <p>
                        <b>Brand: </b>
                        <span>
                            {STORE_BRAND_LABELS[initial.brand as StoreBrand] ??
                                initial.brand ??
                                ''}
                        </span>
                    </p>
                    <p>
                        <b>Status: </b>
                        <span className={hasStatusChanged ? style.changed : ''}>
                            {showDifference && hasStatusChanged
                                ? difference.status
                                : initial.status}
                        </span>
                    </p>
                    <div>
                        <b>Opening hours: </b>

                        <ul>
                            {WEEKDAYS.map((weekday, i) => (
                                <li key={weekday}>
                                    <b className={style.weekday_label}>
                                        {weekday}:{' '}
                                    </b>
                                    <span
                                        className={
                                            difference.hours[i] !== undefined
                                                ? style.changed
                                                : ''
                                        }
                                    >
                                        {showDifference &&
                                        difference.hours[i] !== undefined
                                            ? difference.hours[i]
                                            : initial.hours[i]}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <b>Services: </b>
                        <ul>
                            {displayedServices.map((service) => (
                                <li key={service}>
                                    <span className={getServiceClass(service)}>
                                        {service}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            );
        }
    );

    DifferenceDisplay.displayName = 'DifferenceDisplay';

    const SelectionIcons = (props: {
        onChange: (selected: boolean) => void;
        selected: boolean;
    }) => {
        return props.selected ? (
            <MinusCircleOutlined
                className={style.remove_icon}
                onClick={() => props.onChange(false)}
            />
        ) : (
            <PlusCircleOutlined
                className={style.add_icon}
                onClick={() => props.onChange(true)}
            />
        );
    };

    return (
        <>
            <ScrollMarker
                elems={allFilteredDiff}
                predicate={(d) => deselectionMap[d.initial.nref]}
            />

            <div className={style.container}>
                <div className={style.top_content}>
                    <PageTopBar
                        title="Store differences"
                        goBack={{
                            callback: () => navigation.goToStores(),
                            text: 'Back to stores',
                        }}
                    >
                        {store.coordSuspects.size > 0 && (
                            <Button
                                variant="secondary"
                                onClick={() => setShowCoordModal(true)}
                            >
                                Review coordinate adjustments (
                                {store.coordSuspects.size})
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            loading={store.isUpdatingStores}
                            disabled={
                                !!filter ||
                                allFilteredDiff.length === 0 ||
                                store.isUpdatingStores
                            }
                            onClick={uploadChanges}
                        >
                            Upload changes
                        </Button>
                    </PageTopBar>

                    <div className="action_zone_above_table">
                        <div className="table_header_options_bar">
                            <SearchInput
                                searchValue={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                            <div className={style.select_buttons}>
                                <Button
                                    variant="secondary"
                                    onClick={() =>
                                        setMultipleDeselection(
                                            allFilteredDiff.map(
                                                (d) => d.initial.nref
                                            ),
                                            true
                                        )
                                    }
                                >
                                    Deselect All
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() =>
                                        setMultipleDeselection(
                                            allFilteredDiff.map(
                                                (d) => d.initial.nref
                                            ),
                                            false
                                        )
                                    }
                                >
                                    Select All
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                {store.importWarnings && (
                    <div className={style.import_warnings}>
                        {store.importWarnings.missingColumns.length > 0 && (
                            <div className={style.warning_row}>
                                <b>Expected columns not found</b> — existing
                                values were preserved:{' '}
                                {store.importWarnings.missingColumns
                                    .map((c) => `"${c}"`)
                                    .join(', ')}
                            </div>
                        )}
                        {store.importWarnings.unknownColumns.length > 0 && (
                            <div className={style.warning_row}>
                                <b>Unrecognized columns were ignored:</b>{' '}
                                {store.importWarnings.unknownColumns
                                    .map((c) => `"${c}"`)
                                    .join(', ')}
                            </div>
                        )}
                    </div>
                )}
                <div className={style.section}>
                    <h1 className={style.section_header}>
                        Additions ({filteredAddedDiff.length})
                    </h1>
                    {filteredAddedDiff.map((difference) => (
                        <div
                            key={difference.initial.nref}
                            className={
                                deselectionMap[difference.initial.nref]
                                    ? style.deselected
                                    : ''
                            }
                        >
                            <h2 className={style.nref_header}>
                                {' '}
                                <b>Nref: </b>
                                {difference.initial.nref}
                                <SelectionIcons
                                    selected={
                                        !deselectionMap[difference.initial.nref]
                                    }
                                    onChange={(selected) =>
                                        setDeselection(
                                            difference.initial.nref,
                                            !selected
                                        )
                                    }
                                />
                            </h2>
                            <DifferenceDisplay
                                difference={difference}
                                showDifference={false}
                            />
                        </div>
                    ))}
                </div>
                <div>
                    <h1 className={style.section_header}>
                        Differences ({filteredDiff.length})
                    </h1>
                    {filteredDiff.map((difference) => (
                        <div
                            key={difference.initial.nref}
                            className={
                                deselectionMap[difference.initial.nref]
                                    ? style.deselected
                                    : ''
                            }
                        >
                            <h2 className={style.nref_header}>
                                {' '}
                                <b>Nref: </b>
                                {difference.initial.nref}
                                <SelectionIcons
                                    selected={
                                        !deselectionMap[difference.initial.nref]
                                    }
                                    onChange={(selected) =>
                                        setDeselection(
                                            difference.initial.nref,
                                            !selected
                                        )
                                    }
                                />
                            </h2>
                            <div className={style.dual_box_holder}>
                                <DifferenceDisplay
                                    difference={difference}
                                    showDifference={false}
                                />
                                <DifferenceDisplay
                                    difference={difference}
                                    showDifference={true}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {showCoordModal &&
                store.coordSuspects.size > 0 &&
                store.originalParsedStore && (
                    <CoordPrecisionModal
                        suspects={store.coordSuspects}
                        differences={coordDifferences}
                        onConfirm={handleCoordReviewConfirm}
                        onGoBack={() => setShowCoordModal(false)}
                    />
                )}
            {uploadResult && (
                <BigModal
                    header={<h2>Import complete</h2>}
                    onClose={() => {
                        setUploadResult(null);
                        navigation.replaceToStores();
                    }}
                >
                    <p>
                        <b>Applied:</b> {uploadResult.applied.added}{' '}
                        addition(s), {uploadResult.applied.modified}{' '}
                        modification(s)
                    </p>
                    <p>
                        <b>Skipped ({uploadResult.skipped.length}):</b>
                    </p>
                    <ul>
                        {uploadResult.skipped.map((entry) => (
                            <li key={entry.nref ?? String(entry.id)}>
                                <b>{entry.nref ?? `id:${entry.id}`}</b> —{' '}
                                {entry.reason === 'brand-mismatch'
                                    ? `Belongs to ${
                                          entry.existingBrand ??
                                          'the other brand'
                                      }`
                                    : 'Store not found in the system'}
                            </li>
                        ))}
                    </ul>
                </BigModal>
            )}
        </>
    );
});
